import React, { useState } from 'react';
import {
  Box,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { adminAPI } from '../../services/api';
import Panel from '../crt/Panel';
import StatusChip from './StatusChip';

const STATUSES = ['all', 'success', 'failed', 'running', 'pending'];

const RunsTab: React.FC = () => {
  const [jobName, setJobName] = useState('');
  const [status, setStatus] = useState<string>('all');
  const [page, setPage] = useState(1);
  const perPage = 50;

  const { data: schedule } = useQuery({
    queryKey: ['admin-schedule-jobnames'],
    queryFn: adminAPI.getSchedule,
    staleTime: 60000,
  });

  const { data } = useQuery({
    queryKey: ['admin-runs', jobName, status, page],
    queryFn: () =>
      adminAPI.getRuns({
        job_name: jobName || undefined,
        status: status === 'all' ? undefined : status,
        page,
        per_page: perPage,
      }),
    staleTime: 15000,
  });

  const runs = data?.runs || [];
  const totalPages = data?.pages || 1;

  const fmtDur = (s: number | null | undefined) => {
    if (s == null) return '—';
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}m${rem ? `${rem}s` : ''}`;
  };

  return (
    <Panel label="JOB RUN HISTORY">
      <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          select
          label="Job"
          value={jobName}
          onChange={(e) => { setJobName(e.target.value); setPage(1); }}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="">All jobs</MenuItem>
          {(schedule?.jobs || []).map((j: any) => (
            <MenuItem key={j.job_name} value={j.job_name}>{j.job_name}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          label="Status"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          sx={{ minWidth: 140 }}
        >
          {STATUSES.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
          {data?.total ?? 0} runs match
        </Typography>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>STARTED</TableCell>
              <TableCell>JOB</TableCell>
              <TableCell>STATUS</TableCell>
              <TableCell align="right">DURATION</TableCell>
              <TableCell align="right">RECORDS</TableCell>
              <TableCell>ERROR</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((r: any) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {r.started_at ? new Date(r.started_at).toLocaleString() : '—'}
                </TableCell>
                <TableCell>{r.job_name}</TableCell>
                <TableCell><StatusChip status={r.status} /></TableCell>
                <TableCell align="right">{fmtDur(r.duration_seconds)}</TableCell>
                <TableCell align="right">{(r.records_processed ?? 0).toLocaleString()}</TableCell>
                <TableCell sx={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.error_message || ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            size="small"
          />
        </Box>
      )}
    </Panel>
  );
};

export default RunsTab;
